import React from 'react';
import {
  Card,
  Stack,
  Text,
  Radio,
  Group,
  Button,
  Textarea,
  Badge,
} from '@mantine/core';
import { IconMessage, IconSend, IconCheck, IconX } from '@tabler/icons-react';
import { DiagnosticReport } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import {
  DiagnosticReportReviewAction,
  DiagnosticReportReviewStatus,
} from '../../lims-types';
import { ReviewStatusData } from '../hooks/useReviewStatus';

// -----------------------------------------------------------------------------
interface ReviewCommentsCardProps {
  diagnosticReport: DiagnosticReport;
  reviewStatusData: ReviewStatusData | null;
  currentUserReviewStatus: DiagnosticReportReviewStatus | null;
  reviewStatus: DiagnosticReportReviewAction;
  setReviewStatus: (status: DiagnosticReportReviewAction) => void;
  reviewComment: string;
  setReviewComment: (comment: string) => void;
  submittingComment: boolean;
  onSubmitComment: () => void;
}

// -----------------------------------------------------------------------------
export const ReviewCommentsCard: React.FC<ReviewCommentsCardProps> = ({
  diagnosticReport,
  reviewStatusData,
  currentUserReviewStatus,
  reviewStatus,
  setReviewStatus,
  reviewComment,
  setReviewComment,
  submittingComment,
  onSubmitComment,
}) => {
  const { t } = useTranslation('lims');

  // -----------------------------------------------------------------------------
  const getInstructionText = () => {
    if (diagnosticReport.status === 'final') {
      return t('review.comments.instructions.finalized');
    }
    if (reviewStatusData?.allApproved) {
      return t('review.comments.instructions.allApproved');
    }
    if (
      diagnosticReport.status === 'preliminary' &&
      currentUserReviewStatus === 'pending'
    ) {
      return t('review.comments.instructions.pendingReview');
    }
    if (
      diagnosticReport.status === 'preliminary' &&
      currentUserReviewStatus === 'changes-requested'
    ) {
      return t('review.comments.instructions.changesRequested');
    }
    return t('review.comments.instructions.default');
  };

  // ---------------------------------------------------------------------------
  const showReviewOptions =
    diagnosticReport.status === 'preliminary' &&
    !reviewStatusData?.allApproved &&
    (currentUserReviewStatus === 'pending' ||
      currentUserReviewStatus === 'changes-requested');

  // ---------------------------------------------------------------------------
  return (
    <Card shadow="sm" padding="md" withBorder>
      <Stack>
        <Stack gap={0}>
          <Group justify="space-between">
            <Text size="lg" fw={600}>
              {t('review.comments.title')}
            </Text>
            {currentUserReviewStatus && (
              <Badge
                variant="light"
                color={
                  currentUserReviewStatus === 'approved'
                    ? 'green'
                    : currentUserReviewStatus === 'changes-requested'
                      ? 'orange'
                      : 'blue'
                }
              >
                {currentUserReviewStatus === 'approved'
                  ? t('review.comments.statusLabels.approved')
                  : currentUserReviewStatus === 'changes-requested'
                    ? t('review.comments.statusLabels.changesRequested')
                    : t('review.comments.statusLabels.pending')}
              </Badge>
            )}
          </Group>

          <Text size="sm" c="dimmed">
            {getInstructionText()}
          </Text>
        </Stack>

        {showReviewOptions && (
          <Radio.Group
            value={reviewStatus}
            onChange={(value) =>
              setReviewStatus(value as DiagnosticReportReviewAction)
            }
          >
            <Stack gap="xs">
              <Radio
                value="comment"
                label={
                  <Group gap="xs">
                    <IconMessage size={16} color="blue" />
                    <Text>{t('review.comments.radioOptions.addComment')}</Text>
                  </Group>
                }
              />

              <Radio
                value="approved"
                label={
                  <Group gap="xs">
                    <IconCheck size={16} color="green" />
                    <Text>
                      {t('review.comments.radioOptions.approveReport')}
                    </Text>
                  </Group>
                }
              />
              <Radio
                value="changes-requested"
                label={
                  <Group gap="xs">
                    <IconX size={16} color="red" />
                    <Text>
                      {t('review.comments.radioOptions.requestChanges')}
                    </Text>
                  </Group>
                }
              />
            </Stack>
          </Radio.Group>
        )}
        <Textarea
          label={
            reviewStatus === 'comment'
              ? t('review.comments.textareaLabels.comment')
              : t('review.comments.textareaLabels.review')
          }
          value={reviewComment}
          onChange={(e) => setReviewComment(e.currentTarget.value)}
          disabled={submittingComment}
          withAsterisk={reviewStatus !== 'comment'}
          autosize
          minRows={3}
        />
        <Group justify="flex-end">
          <Button
            leftSection={<IconSend size={16} />}
            onClick={onSubmitComment}
            loading={submittingComment}
            disabled={!reviewComment.trim()}
            color={
              reviewStatus === 'approved'
                ? 'green'
                : reviewStatus === 'changes-requested'
                  ? 'red'
                  : 'blue'
            }
          >
            {reviewStatus === 'approved'
              ? t('review.comments.buttons.approveReport')
              : reviewStatus === 'changes-requested'
                ? t('review.comments.buttons.requestChanges')
                : t('review.comments.buttons.addComment')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};
